import { useCallback, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import SafeImage from '@components/SafeImage';
import MediaLightbox from '@components/MediaLightbox';

const THUMBNAIL_SIZE = 64;

const Thumbnail = ({ item, onClick, onRefresh }) => {
    const [failed, setFailed] = useState(false);

    if (failed) return null;

    const handleError = async () => {
        if (!item.refresh || !(await onRefresh(item))) setFailed(true);
    };

    return (
        <Box
            onClick={onClick}
            sx={{
                position: 'relative',
                width: THUMBNAIL_SIZE,
                height: THUMBNAIL_SIZE,
                borderRadius: 1,
                overflow: 'hidden',
                cursor: 'pointer',
                backgroundColor: 'var(--background-secondary, #f5f5f5)',
                flexShrink: 0,
                '&:hover': { opacity: 0.85 },
            }}
        >
            <SafeImage
                src={item.thumbnailUrl}
                alt={item.label || ''}
                onError={handleError}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {item.videoUrl && (
                <PlayCircleOutlineIcon
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: '#fff',
                        filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.6))',
                        pointerEvents: 'none',
                        fontSize: 32,
                    }}
                />
            )}
        </Box>
    );
};

const MediaGallery = ({ media }) => {
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const [refreshedUrls, setRefreshedUrls] = useState({});
    const handleClose = useCallback(() => setLightboxIndex(null), []);

    const items = useMemo(
        () =>
            media?.map(item => {
                const url = refreshedUrls[item.thumbnailUrl];
                return url ? { ...item, thumbnailUrl: url, refresh: undefined } : item;
            }),
        [media, refreshedUrls],
    );

    const refresh = useCallback(async item => {
        try {
            const url = await item.refresh();
            if (!url) return false;
            setRefreshedUrls(current => ({ ...current, [item.thumbnailUrl]: url }));
            return true;
        } catch {
            return false;
        }
    }, []);

    if (!items?.length) return null;

    return (
        <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                {items.map((item, i) => (
                    <Thumbnail
                        key={`${item.thumbnailUrl}-${i}`}
                        item={item}
                        onClick={() => setLightboxIndex(i)}
                        onRefresh={refresh}
                    />
                ))}
            </Box>
            <MediaLightbox
                open={lightboxIndex !== null}
                onClose={handleClose}
                items={items}
                initialIndex={lightboxIndex ?? 0}
            />
        </>
    );
};

export default MediaGallery;
